import { useQueryClient } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

import useJWTTokenComposable from '@/composables/useJWTToken.composable';
import { EnumRoutes } from '@/helpers/enums/routes.enum';
import type {
  AuthError,
  AuthSecrets,
  IPortals,
  RegistrationResponse,
  VerificationResendResponse,
} from '@/helpers/interfaces/auth.interface';
import type { ProfileSubmit } from '@/helpers/interfaces/profile.submit.interface';
import {
  ConversationsSsePayloadSchema,
  NotificationSsePayloadSchema,
  OrdersSsePayloadSchema,
  RedisChannels,
  SsePayloadSchema,
  WalletSsePayloadSchema,
  type ConversationsSsePayload,
  type NotificationSsePayload,
  type OrdersSsePayload,
  type WalletSsePayload,
} from '@/helpers/interfaces/sse-event.interface';
import type { UserWithExtraFields } from '@/helpers/interfaces/user.interface';
import { axiosI } from '@/plugins';
import router from '@/router';
import type { RegisterDto } from '@/submodules/dtos/register.dto';
import type { User } from '@/submodules/generated_types/types/user';
import { authApiHandler } from '@/submodules/postman-to-ts/apiHandler/auth';
import { miscApiHandler } from '@/submodules/postman-to-ts/apiHandler/misc';
import { socialApiHandler } from '@/submodules/postman-to-ts/social';

import { useCartStore } from './cart.store';
import { useConversationStore } from './conversation.store';
import { useGroupStore } from './groups.store';
import { useNotificationsStore } from './notifications.store';
import { usePermissionsStore } from './permissoins.store';
import { useWalletStore } from './wallet.store';

interface IAuthStore {
  user: Ref<UserWithExtraFields | User | null>;
  authError: Ref<AuthError | null>;
  initializeAuth: () => Promise<void>;
  register: (_payload: {
    registerDto: RegisterDto;
    clientOrganizationId: number;
    preferredLanguage: string;
  }) => Promise<{
    data?: RegistrationResponse;
    status?: number;
    error?: AxiosError<{
      message?: string;
      statusCode?: number;
      errorCode?: string;
    }>;
  }>;
  login: (_body: { email: string; password: string }) => Promise<any>;
  twoFactorAuthentication: (
    _params: typeof authApiHandler.login.twoFactorAuthentication.params
  ) => Promise<any>;
  verifyToken: (_isResetPassword?: boolean) => Promise<any>;
  resendCode: (
    _params: typeof authApiHandler.login.resendCode.params
  ) => Promise<any>;
  resendVerificationEmail: (_token: string) => Promise<{
    data?: VerificationResendResponse;
    status?: number;
    error?: AxiosError<{
      message?: string;
      statusCode?: number;
      errorCode?: string;
    }>;
  }>;
  refreshAccessToken: (_isInitializeAuth?: boolean) => Promise<any>;
  requestSSO: (_params?: { [key: string]: string | number }) => Promise<any>;
  resetPassword: (_body: { password: string; token: string }) => Promise<any>;
  requestResetPassword: (_body: { email: string }) => Promise<any>;
  logout: (_removeAll?: boolean) => Promise<any>;
  previousRoute: Ref<string | null>;
  setPreviousRoute: (_previous: string | null) => void;
  editUser: (_updateUser: ProfileSubmit) => Promise<any>;
  changePassword: (_body: {
    oldPassword: string;
    newPassword: string;
  }) => Promise<any>;
  shouldLogged: Ref<boolean>;
  shouldHavePermission: Ref<boolean>;
  handleLogout: (_changeRoute?: boolean) => Promise<any>;
  loginSSO: (_superToken: string, _onlyView: boolean) => Promise<any>;
  handleSAMLSSO: (_ssoid: string) => Promise<any>;
  instructorView: Ref<boolean>;
  twoFactorTokenExpiresAt: Ref<string | undefined>;
  shouldResetPassword: Ref<boolean>;
  portals: Ref<IPortals | null>;
  verifyEmail: (_token: string) => Promise<{
    data?: {
      status: string;
      message: string;
    };
    error?: AxiosError<{ errorCode?: string }>;
  }>;
  reportEmail: (_token: string) => Promise<{
    data?: { message?: string };
    status?: number;
    error?: AxiosError<{ errorCode?: string }>;
  }>;
}

export const useAuthStore = defineStore('AuthStore', (): IAuthStore => {
  /**
  >>>>>>>>>>>>>>> state variables >>>>>>>>>>>>>>>
  */

  const authError = ref<AuthError | null>(null);
  const user = ref<UserWithExtraFields | null>(null);
  const secrets = ref<AuthSecrets | null>(null);
  const previousRoute = ref<string | null>(null);
  const shouldLogged = ref<boolean>(false);
  const shouldHavePermission = ref<boolean>(false);
  const instructorView = ref<boolean>(false);
  const twoFactorTokenExpiresAt = ref<string | undefined>(undefined);
  const shouldResetPassword = ref(false);
  const queryClient = useQueryClient();
  // this is temporary until we recieved it in the auth API
  const portals: Ref<IPortals | null> = ref(null);
  let timeoutHandle: ReturnType<typeof setTimeout>;

  //________________ Check if there is a token and set the user
  async function initializeAuth() {
    const parseJSON = <T>(value: string | null): T | null => {
      if (!value) return null;
      try {
        return JSON.parse(value) as T;
      } catch {
        return null;
      }
    };

    const storedPreviousRoute = localStorage.getItem('previousRoute');
    setPreviousRoute(storedPreviousRoute);

    user.value = parseJSON<UserWithExtraFields>(localStorage.getItem('user'));
    portals.value = parseJSON<IPortals>(localStorage.getItem('portals'));

    const access = localStorage.getItem('access');
    const refresh = localStorage.getItem('refresh');

    const twoFactorExpiry = localStorage.getItem('twoFactorTokenExpiresAt');
    twoFactorTokenExpiresAt.value = twoFactorExpiry || undefined;
    instructorView.value = sessionStorage.getItem('instructorView') === 'true';

    usePermissionsStore().setPermissions();

    if (!access) {
      await handleLogout(false);
      return;
    }

    if (!refresh) {
      await logout();
      return;
    }

    void handleExpiredAccessToken(true);

    // Only initialize non-auth stores if user is verified
    // Unverified users are in restricted session — no cart, conversations, groups, SSE
    if (user.value?.isVerified !== false) {
      void subscribeToServerSSe();
      handleVariousFunctionsFromOtherStores();
      void useGroupStore().getUserGroup();
    }
  }
  async function handleExpiredAccessToken(isInitializeAuth = false) {
    const token = useJWTTokenComposable();
    timeoutHandle = setTimeout(handleExpiredAccessToken, 15000);
    if (!token) return;

    if (token.exp - Date.now() / 1000 > 30) {
      // not expired
      if (isInitializeAuth) {
        refreshAccessToken(isInitializeAuth);
        await verifyToken();
      }
    } else {
      // expired
      if (isInitializeAuth) {
        // Don't logout if on verify-email or report-email page —
        // the user needs to stay on the page to see the verification result
        const currentPath = router.currentRoute.value.path || '';
        if (
          currentPath.includes('verify-email') ||
          currentPath.includes('report-email')
        ) {
          return;
        }
        handleLogout();
      } else await refreshAccessToken();
    }
  }
  /**
>>>>>>>>>>>>>>> functions >>>>>>>>>>>>>>>
*/
  async function handleLogout(changeRoute: boolean = true) {
    clearTimeout(timeoutHandle);
    secrets.value = null;
    user.value = null;
    authError.value = null;
    portals.value = null;
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('user');
    localStorage.removeItem('portals');
    useCartStore().cart = null;
    closeSse();
    if (changeRoute) {
      // Always redirect to login from verify-email/report-email pages
      const currentRouteName = router.currentRoute.value.name;
      if (
        currentRouteName === EnumRoutes.auth.verifyEmail.name ||
        currentRouteName === EnumRoutes.auth.reportEmail.name
      ) {
        await router.replace({ name: EnumRoutes.auth.login.name });
        return;
      }

      // to prevent calling permissions store twice when load the app

      // get the permissions on auth change
      await usePermissionsStore().initializePermissions();
      // check the permission for the current route
      const currentRoute = router.currentRoute.value;
      const havePermission = usePermissionsStore().checkAuthentication(
        currentRoute.meta
      );
      if (havePermission) return;
      else {
        if (import.meta.env.VITE_ALLOW_LANDING === 'false')
          await router.replace({ name: EnumRoutes.auth.login.name });
        else await router.replace({ name: EnumRoutes.landing.root.name });
      }
    }
  }
  function handleVariousFunctionsFromOtherStores() {
    //________________ Query Client ________________
    queryClient.clear();
    //________________ Cart ________________
    useCartStore().initializeCartStore();
    //________________ Conversations ________________
    void useConversationStore().getUnreadConversationCount();
  }
  let eventSource: EventSource | null = null;

  const closeSse = () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
  };

  const subscribeToServerSSe = async () => {
    if (!user.value) return;

    const { path, params } = miscApiHandler.sSE.subscribeToSSE;
    const baseURL = import.meta.env.VITE_APP_API_BASE_URL;

    const info: typeof params = {
      token: localStorage.getItem('access') ?? '',
    };
    const url = baseURL + path() + `?token=${info.token}`;
    try {
      eventSource = new EventSource(url);
      eventSource.onerror = () => {};
      eventSource.onmessage = (event) => {
        const result = SsePayloadSchema.safeParse(JSON.parse(event.data));
        if (!result.success) return;
        const data = result.data;
        let notificationData: NotificationSsePayload | undefined = undefined;
        let orderData: OrdersSsePayload | undefined = undefined;
        let walletData: WalletSsePayload | undefined = undefined;
        let conversationData: ConversationsSsePayload | undefined = undefined;
        switch (data.channel) {
          case RedisChannels.NOTIFICATIONS:
            notificationData =
              NotificationSsePayloadSchema.safeParse(data).data;
            if (!notificationData) return;
            useNotificationsStore().getNotifications();
            break;
          case RedisChannels.ORDERS:
            orderData = OrdersSsePayloadSchema.safeParse(data).data;
            if (!orderData) return;
            useCartStore().initializeCartStore(true);
            break;
          case RedisChannels.WALLET:
            walletData = WalletSsePayloadSchema.safeParse(data).data;
            if (!walletData) return;
            useWalletStore().getMyWallet();
            break;
          case RedisChannels.CONVERSATIONS:
            conversationData =
              ConversationsSsePayloadSchema.safeParse(data).data;
            if (!conversationData) return;
            useConversationStore().getUnreadConversationCount();
            useConversationStore().receivedMessageHandler(
              conversationData.data.conversation
            );
            break;
          default:
            break;
        }
      };
    } catch (error: any) {
      return error;
    }
  };

  async function handleNewAccessToken(
    data: AuthSecrets,
    isInitializeAuth = false
  ) {
    if (data.expiresAt) {
      localStorage.setItem('expiresAt', data.expiresAt);
    }
    if (data.refresh) {
      localStorage.setItem('refresh', data.refresh);
    }
    if (data.access) {
      localStorage.setItem('access', data.access);
      await Promise.all([verifyToken()]);
    }
    if (data.portals) {
      localStorage.setItem('portals', JSON.stringify(data.portals));
      portals.value = data.portals;
    }
    //________________ Notifications ________________
    closeSse();
    subscribeToServerSSe();
    if (isInitializeAuth) return;
    handleVariousFunctionsFromOtherStores();
    // get the permissions on auth change
    await usePermissionsStore().initializePermissions();
    //________________ Group ________________
    useGroupStore().getUserGroup();
  }

  /**
  >>>>>>>>>>>>>>> APIs >>>>>>>>>>>>>>>
  */
  //________________ Register ________________
  async function register({
    registerDto,
    clientOrganizationId,
    preferredLanguage,
  }: {
    registerDto: RegisterDto;
    clientOrganizationId: number;
    preferredLanguage: string;
  }) {
    const { path, method } = authApiHandler.register.register;
    try {
      const { data, status } = await axiosI<RegistrationResponse>(path(), {
        method,
        data: { ...registerDto, preferredLanguage },
        headers: {
          Organization: clientOrganizationId,
        },
      });

      if (data) {
        await handleAuthResponse(data);
      }
      return { data, status };
    } catch (error) {
      return {
        error: error as AxiosError<{
          statusCode?: number;
          message?: string;
        }>,
      };
    }
  }

  async function handleAuthResponse(response: RegistrationResponse) {
    const auth = response.authentication;

    switch (response.status) {
      case 'authenticated': {
        if (!auth?.access || !auth?.refresh) return;
        secrets.value = {
          access: auth.access,
          refresh: auth.refresh,
        };
        twoFactorTokenExpiresAt.value = undefined;
        localStorage.removeItem('twoFactorTokenExpiresAt');
        localStorage.removeItem('guestEmail');
        await handleSetLoginData({
          access: auth.access,
          refresh: auth.refresh,
        } as AuthSecrets);
        void handleExpiredAccessToken();
        break;
      }
      case 'two-factor': {
        if (!auth?.twoFactorToken) return;
        secrets.value = {
          twoFactorToken: auth.twoFactorToken,
          expiresAt: auth.expiresAt?.toString(),
          code: auth.code as AuthSecrets['code'],
          email: auth.email,
        };
        const expiresAt = auth.expiresAt?.toString();
        twoFactorTokenExpiresAt.value = expiresAt;
        if (expiresAt) {
          localStorage.setItem('twoFactorTokenExpiresAt', expiresAt);
        } else {
          localStorage.removeItem('twoFactorTokenExpiresAt');
        }
        localStorage.removeItem('guestEmail');
        await router.replace({
          name: EnumRoutes.auth.twoFactorVerification.name,
          query: {
            token: auth.twoFactorToken,
            email: auth.email,
          },
        });
        break;
      }
      case 'pending-verification': {
        if (auth?.access) {
          localStorage.setItem('access', auth.access);
        }
        if (auth?.refresh) {
          localStorage.setItem('refresh', auth.refresh);
        }
        if (response.user) {
          user.value = response.user as UserWithExtraFields;
          localStorage.setItem('user', JSON.stringify(response.user));
        }
        localStorage.setItem('registeration-status', 'pending-verification');
        if (response.verification?.token) {
          localStorage.setItem(
            'verification-resend-token',
            response.verification.token
          );
          localStorage.setItem(
            'verification-email',
            response.verification.email
          );
        }
        await router.replace({ name: EnumRoutes.auth.verifyEmail.name });
        break;
      }
      case 'pending-approval':
        // No tokens, no redirect — the caller (RegisterForm) handles the UI
        break;
      default:
        break;
    }
  }

  //________________ login ________________
  async function login(body: { email: string; password: string }) {
    const { path, method } = authApiHandler.login.login;

    try {
      const { data, status } = await axiosI<RegistrationResponse>(path(), {
        method,
        data: body,
      });

      localStorage.removeItem('guestEmail');
      await handleAuthResponse(data);

      return { data, status };
    } catch (error: any) {
      authError.value = error.response?.data;
      return error;
    }
  }

  //________________ twoFactorAuthentication ________________
  async function twoFactorAuthentication(
    params: typeof authApiHandler.login.twoFactorAuthentication.params
  ): Promise<any> {
    const { path, method } = authApiHandler.login.twoFactorAuthentication;
    try {
      const { data, status } = await axiosI<AuthSecrets>(path(), {
        method,
        params,
      });
      await handleSetLoginData(data);
      return { data, status };
    } catch (error: any) {
      return error;
    }
  }

  const handleSetLoginData = async (data: AuthSecrets) => {
    await handleNewAccessToken(data);
    sessionStorage.removeItem('instructorView');
    localStorage.removeItem('registeration-status');
    await handleStoredSsoid();
    if (previousRoute.value) {
      await router.push(previousRoute.value);
    } else {
      await router.replace({ name: EnumRoutes.dashboard.root.name });
    }
  };

  //handle if ssoid token is present
  const handleStoredSsoid = async () => {
    const ssoid = localStorage.getItem('ssoid');
    if (ssoid) {
      localStorage.removeItem('ssoid');
      return await handleSAMLSSO(ssoid);
    }
    return;
  };

  //________________ verifyToken ________________

  async function verifyToken(isResetPassword?: boolean) {
    const { path, method } = authApiHandler.verifyToken;
    try {
      if (isResetPassword) return;
      const { data, status } = await axiosI<UserWithExtraFields>(path(), {
        method,
      });
      user.value = data;
      shouldResetPassword.value = user.value.forceResetPassword;
      localStorage.setItem('user', JSON.stringify(data));
      const { path: path2, method: method2 } =
        authApiHandler.sSO.generateSSOToken;
      axiosI(path2(), {
        method: method2,
        params: { portal: 'LMS' },
      });
      return { data, status };
    } catch (error: any) {
      return error;
    }
  }

  //________________ resendCode ________________
  async function resendCode(
    params: typeof authApiHandler.login.resendCode.params
  ) {
    const { path, method } = authApiHandler.login.resendCode;
    try {
      const { data, status } = await axiosI<{
        message: string;
        expiresAt: string;
      }>(path(), {
        method,
        params,
      });
      twoFactorTokenExpiresAt.value = data.expiresAt;
      if (twoFactorTokenExpiresAt.value)
        localStorage.setItem(
          'twoFactorTokenExpiresAt',
          twoFactorTokenExpiresAt.value
        );
      return { data, status };
    } catch (error: any) {
      return error;
    }
  }

  async function resendVerificationEmail(token: string) {
    const { path, method } =
      authApiHandler.emailVerification.resendEmailVerification;
    try {
      const { data, status } = await axiosI<VerificationResendResponse>(
        path(),
        {
          method,
          data: {
            token,
          },
        }
      );
      return { data, status };
    } catch (error) {
      return {
        error: error as AxiosError<{
          statusCode?: number;
          message?: string;
          errorCode?: string;
        }>,
      };
    }
  }

  //________________ refresh access token ________________
  async function refreshAccessToken(isInitializeAuth = false) {
    const refreshToken = localStorage.getItem('refresh');
    const { path, method } = authApiHandler.refresh_New;
    try {
      const { data, status } = await axiosI<AuthSecrets>(path(), {
        method,
        data: {
          token: refreshToken,
        },
      });
      await handleNewAccessToken(data, isInitializeAuth);
      return { data, status };
    } catch (error: any) {
      return error;
    }
  }
  // handle SAML SSO
  async function handleSAMLSSO(ssoid: string) {
    const { path, method } = authApiHandler.sAML.authenticateUser;
    try {
      const { data, status } = await axiosI(path({ ssoid }), {
        method,
      });
      // return { data, status };
      //the data is of type HTML, I need to render it in the browser
      const parser = new DOMParser();
      const html = parser.parseFromString(data, 'text/html');
      const form = html.querySelector('form');
      if (form) {
        document.body.appendChild(form);
        form.submit();
      }
      return { data, status };
    } catch (error: any) {
      return error;
    }
  }

  //________________ SSO ________________
  async function loginSSO(superToken: string, _onlyView: boolean) {
    const { path, method } = authApiHandler.sSO.getTokensUsingSSO;
    try {
      const { data, status } = await axiosI<AuthSecrets>(path(), {
        method,
        params: {
          token: superToken,
        },
        headers: {
          Authorization: `Bearer ${superToken}`,
        },
      });

      await handleNewAccessToken(data);
      handleExpiredAccessToken();
      return { data, status };
    } catch (error: any) {
      sessionStorage.removeItem('instructorView');
      instructorView.value = false;
      return error;
    }
  }
  async function requestSSO(params?: { [key: string]: string | number }) {
    try {
      const { path, method } = authApiHandler.sSO.generateSSOToken;
      const { data } = await axiosI(path(), {
        method,
        params,
      });
      return data;
    } catch (error) {
      return error;
    }
  }

  //________________ Reset password ________________

  async function resetPassword(body: { password: string; token: string }) {
    const { path, method } = authApiHandler.resetPassword.resetPassword;
    try {
      const { status } = await axiosI(path(), {
        method,
        data: body,
      });
      return status;
    } catch (error: any) {
      return error.response.status;
    }
  }

  //________________ requestResetPassword  ________________
  async function requestResetPassword(body: { email: string }) {
    const { path, method } = authApiHandler.resetPassword.requestResetPassword;
    try {
      const { data, status } = await axiosI(path(), {
        method,
        data: { ...body, portal: 'student' },
      });
      return { data, status };
    } catch (error: any) {
      return error;
    }
  }

  //________________ logout ________________

  async function logout(removeAll?: boolean) {
    const { path, method } = authApiHandler.logout;
    try {
      const { data, status } = await axiosI(path(), {
        method,
        data: {
          removeAll,
        },
      });
      await handleLogout();
      return { data, status };
    } catch (error: any) {
      // API failed (e.g. token expired, user deleted) — still clear local state
      await handleLogout();
      return error;
    }
  }

  //________________ set previous route ________________
  // set the previous route to redirect to it after login
  function setPreviousRoute(previous: string | null) {
    previous
      ? localStorage.setItem('previousRoute', previous)
      : localStorage.removeItem('previousRoute');
    previousRoute.value = previous;
  }

  // ____________________ Edit User ____________________
  async function editUser(updateUser: ProfileSubmit) {
    const { path, method } = socialApiHandler.users.updateUser;
    const { data, status } = await axiosI(path(), {
      method,
      data: updateUser,
    });
    await verifyToken();
    return { data, status };
  }

  async function changePassword(body: {
    oldPassword: string;
    newPassword: string;
  }) {
    const { path, method } = socialApiHandler.users.changePassword;
    try {
      const data = await axiosI(path(), {
        method,
        data: body,
      });
      if (shouldResetPassword.value)
        await verifyToken().catch(async (error) => {
          if (error.response.data.errorCode == 401) {
            await refreshAccessToken().catch(() => {
              handleLogout();
            });
          }
        });
      return data;
    } catch (error: any) {
      return error;
    }
  }
  async function verifyEmail(token: string) {
    const { path, method } = authApiHandler.emailVerification.verifyEmail;
    try {
      const { data } = await axiosI<{
        status: string;
        message: string;
      }>(path(), {
        method,
        params: {
          token,
        },
      });
      // Clear restricted session data — user must login again
      localStorage.removeItem('registeration-status');
      localStorage.removeItem('verification-resend-token');
      localStorage.removeItem('verification-email');
      return { data };
    } catch (error) {
      return { error: error as AxiosError<{ errorCode?: string }> };
    }
  }

  async function reportEmail(token: string) {
    const { path, method } = authApiHandler.emailVerification.reportEmail;
    try {
      const { data, status } = await axiosI<{ message?: string }>(path(), {
        method,
        data: { token },
      });
      return { data, status };
    } catch (error) {
      return { error: error as AxiosError<{ errorCode?: string }> };
    }
  }

  return {
    user,
    authError,
    initializeAuth,
    register,
    login,
    twoFactorAuthentication,
    verifyToken,
    resendCode,
    resendVerificationEmail,
    refreshAccessToken,
    resetPassword,
    requestResetPassword,
    logout,
    previousRoute,
    setPreviousRoute,
    editUser,
    changePassword,
    shouldLogged,
    shouldHavePermission,
    handleLogout,
    loginSSO,
    handleSAMLSSO,
    requestSSO,
    instructorView,
    twoFactorTokenExpiresAt,
    shouldResetPassword,
    portals,
    verifyEmail,
    reportEmail,
  };
});
